"use client"
import { useGSAP } from "@gsap/react";
import clsx from "clsx";
import gsap from "gsap";
import { useRef, useState, memo } from "react";
import AnimatedLink from "@/components/animated-link";
import { STORIES_BASE_PATH } from "@/constants/routes";
import { useApp } from "@/context/AppContext";
import useBatchCardAnimation from "@/hooks/useBatchCardAnimation";
import useImageLoad from "@/hooks/useImageLoad";
import RecordCardImageGroup from "./record-card-image-group";

function RecordCard({ recordCategoryType = 'curated', className = "", isParentInView, data, index = 0, intertia = 1, isFirstInBatch = false, batchIndex, onBatchEnter }) {
  const { rootRef, scrollRef, isIn } = useBatchCardAnimation({
    index,
    isParentInView,
    isFirstInBatch,
    batchIndex,
    onBatchEnter,
  });

  const { setRecordCategoryType } = useApp()

  const [isHoverIn, setIsHoverIn] = useState(false);
  const imageGroupRef = useRef(null);
  const {imageRef, onLoad} = useImageLoad()

  const handleIn = () => {
    setIsHoverIn(true)
  }

  const handleOut = () => {
    setIsHoverIn(false)
  }

  const handleClick = () => {
    setRecordCategoryType(recordCategoryType)
  }

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add(
      {
        hasMotion: "(prefers-reduced-motion: no-preference)",
        hasNoMotion: "(prefers-reduced-motion: reduce)",
      },
      (context) => {
        const { hasMotion, hasNoMotion } = context.conditions;

        if (hasMotion) {
          gsap.killTweensOf([imageGroupRef.current, imageRef.current]);
          gsap.to(imageGroupRef.current, {
            scale: isHoverIn ? 1.05 : 1,
            rotate: isHoverIn ? 5 : 0,
            ease: 'expo.out',
            duration: isHoverIn ? 1.2 : 0.9
          })
        }
      }
    )

  }, [isHoverIn])

  return (
    <div ref={scrollRef}>
      <AnimatedLink href={`${STORIES_BASE_PATH}/record-gallery/${data.category}/${data.slug}`} transitionName="overlay-in"
        className={
          clsx(
            "flex flex-col items-center gap-y-(--space-5) opacity-[0.001] will-change-transform",
          )
        }
        ref={rootRef}
        onFocus={handleIn}
        onBlur={handleOut}
        onMouseEnter={handleIn}
        onMouseLeave={handleOut}
        onClick={handleClick}
      >
        <div className="w-full" ref={imageGroupRef}>
          <RecordCardImageGroup data={data} ref={imageRef} isIn={isIn} isHoverIn={isHoverIn} onLoad={onLoad}/>
        </div>
        <div className="flex flex-col gap-y-2.5 md:gap-y-[0.58rem] lg:gap-y-[0.625rem] xl:gap-y-(--space-5) w-full px-[10%]">
          <div className="heading-3-small text-pretty lg:heading-5-medium xl:heading-4">
            {data.date}
          </div>
          <p className="body-1-small md:body-3-small lg:body-3 xl:body-2 w-full">
            {data.text}
          </p>
        </div>
      </AnimatedLink>
    </div>
  )
}

export default memo(RecordCard);
